import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';

// API URL for backend calls
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const Login = () => {
  const navigate = useNavigate();
  const { login, error } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState('');

  // Import Firebase auth and providers
  const { useEffect } = React;
  const [firebaseLoaded, setFirebaseLoaded] = useState(false);
  
  useEffect(() => {
    // Dynamically import Firebase to avoid issues with SSR
    const loadFirebase = async () => {
      try {
        await import('../utils/firebase');
        setFirebaseLoaded(true);
      } catch (err) {
        console.error('Error loading Firebase:', err);
        setFormError('Error loading authentication providers');
      }
    };
    
    loadFirebase();
  }, []);
  
  // Function to handle social login (Google, Microsoft)
  const handleSocialLogin = async (provider) => {
    setFormError('');
    setIsLoading(true);
    
    if (!firebaseLoaded) {
      setFormError('Authentication system is still loading. Please try again.');
      setIsLoading(false);
      return;
    }
    
    try {
      // Import Firebase auth and providers on demand
      const { auth, googleProvider, microsoftProvider } = await import('../utils/firebase');
      const { signInWithPopup } = await import('firebase/auth');
      
      // Select the right provider
      let authProvider;
      if (provider === 'google') {
        authProvider = googleProvider;
      } else if (provider === 'microsoft') {
        authProvider = microsoftProvider;
      } else {
        throw new Error(`Unsupported provider: ${provider}`);
      }
      
      // Sign in with popup
      const result = await signInWithPopup(auth, authProvider);
      
      // After successful firebase auth, send the token to our backend
      const idToken = await result.user.getIdToken();
      
      // Now authenticate with our backend using the Firebase token
      const response = await fetch(`${API_URL}/api/auth/social-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          provider, 
          idToken,
          email: result.user.email,
          name: result.user.displayName || `${provider.charAt(0).toUpperCase() + provider.slice(1)} User`
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        console.log('Social login successful, processing user data:', data.user);
        // Pass the user object as first parameter (not just email) and token as second parameter
        await login(data.user, data.token, true); // true indicates social login
        navigate('/');
      } else {
        setFormError(data.error || `Failed to sign in with ${provider}`);
      }
    } catch (err) {
      console.error(`${provider} login error:`, err);
      // Check for user cancellation which is not an error
      if (err.code === 'auth/cancelled-popup-request' || 
          err.code === 'auth/popup-closed-by-user') {
        console.log('User cancelled the login process');
      } else {
        setFormError(`Error signing in with ${provider}. ${err.message || 'Please try again.'}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    // Reset error
    setFormError('');
    
    // Basic validation
    if (!email || !password) {
      setFormError('Email and password are required');
      return;
    }
    
    setIsLoading(true);
    try {
      const success = await login(email, password);
      if (success) {
        navigate('/');
      }
    } catch (err) {
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <style>{`
        .login-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 50%, #16213e 100%);
          position: relative;
          overflow: hidden;
        }
        .login-page::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(102, 126, 234, 0.1) 0%, transparent 50%);
          animation: pulse 15s ease-in-out infinite;
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }
        .login-card {
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 24px;
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
        }
        .login-title {
          font-size: 2.5rem;
          font-weight: 700;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .login-input,
        .login-input:focus,
        .login-input:active,
        .login-page .form-control {
          background: rgba(255, 255, 255, 0.05) !important;
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
          border-radius: 12px !important;
          color: #ffffff !important;
          padding: 12px 16px !important;
          transition: all 0.3s ease;
          -webkit-text-fill-color: #ffffff !important;
        }
        .login-input:focus {
          background: rgba(255, 255, 255, 0.08) !important;
          border-color: rgba(102, 126, 234, 0.5) !important;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
        }
        .login-input::placeholder {
          color: rgba(255, 255, 255, 0.4) !important;
        }
        .social-btn {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 12px;
          color: white;
          padding: 12px 20px;
          transition: all 0.3s ease;
          font-weight: 500;
        }
        .social-btn:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.25);
          color: white;
          transform: translateY(-2px);
        }
        .social-btn-google:hover {
          border-color: #ea4335;
        }
        .social-btn-microsoft:hover {
          border-color: #0078d4;
        }
        .login-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          border-radius: 12px;
          padding: 14px 20px;
          font-weight: 600;
          font-size: 1rem;
          transition: all 0.3s ease;
        }
        .login-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(102, 126, 234, 0.4);
        }
        .divider-line {
          flex: 1;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
        }
      `}</style>
      
      <div className="login-page">
        <Container>
          <Row className="justify-content-center">
            <Col xs={12} sm={10} md={8} lg={5} xl={4}>
              <div className="text-center mb-4">
                <Link to="/welcome" style={{ textDecoration: 'none' }}>
                  <h1 className="login-title">
                    <span role="img" aria-label="logo">📊</span> EchoBrief
                  </h1>
                </Link>
                <p style={{ color: 'rgba(255,255,255,0.6)' }}>Sign in to your account</p>
              </div>
              
              <Card className="login-card border-0">
                <Card.Body className="p-4 p-md-5">
                  {(error || formError) && (
                    <Alert variant="danger" className="mb-4">
                      {error || formError}
                    </Alert>
                  )}

                  {/* Social Login Buttons */}
                  <div className="mb-4">
                    <Button 
                      className="social-btn social-btn-google w-100 mb-3 d-flex align-items-center justify-content-center"
                      onClick={() => handleSocialLogin('google')}
                      disabled={isLoading}
                    >
                      <span className="me-2">G</span> Continue with Google
                    </Button>
                    <Button 
                      className="social-btn social-btn-microsoft w-100 d-flex align-items-center justify-content-center"
                      onClick={() => handleSocialLogin('microsoft')}
                      disabled={isLoading}
                    >
                      <span className="me-2">⊞</span> Continue with Microsoft
                    </Button>
                  </div>
                  
                  {/* Divider */}
                  <div className="d-flex align-items-center mb-4">
                    <div className="divider-line"></div>
                    <span className="px-3" style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem' }}>OR</span>
                    <div className="divider-line"></div>
                  </div>
                  
                  <Form onSubmit={handleLogin}>
                    <Form.Group className="mb-3">
                      <Form.Label style={{ color: 'rgba(255,255,255,0.8)', fontWeight: '500' }}>Email</Form.Label>
                      <Form.Control
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email"
                        className="login-input"
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-4">
                      <Form.Label style={{ color: 'rgba(255,255,255,0.8)', fontWeight: '500' }}>Password</Form.Label>
                      <Form.Control
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        className="login-input"
                      />
                    </Form.Group>
                    
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <Form.Check 
                        type="checkbox" 
                        label="Show password" 
                        onChange={() => setShowPassword(!showPassword)}
                        style={{ color: 'rgba(255,255,255,0.6)' }}
                      />
                      <Link to="/forgot-password" style={{ color: '#667eea', textDecoration: 'none', fontSize: '0.9rem' }}>
                        Forgot password?
                      </Link>
                    </div>
                    
                    <Button
                      type="submit"
                      className="login-btn w-100 mb-4"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Signing In...' : 'Sign In'}
                    </Button>
                    
                    <div className="text-center">
                      <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: 0 }}>
                        Don't have an account?{' '}
                        <Link to="/register" style={{ color: '#667eea', textDecoration: 'none', fontWeight: '500' }}>
                          Sign up
                        </Link>
                      </p>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default Login;
